<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AppLogo from '@/components/AppLogo.vue'
import SessaoReplayer from '@/components/SessaoReplayer.vue'
import {
  addSessaoAnnotation,
  ativarCompartilhamentoSessao,
  getSessao,
  loadSessaoEventsIncremental,
  removeSessaoAnnotation,
  revogarCompartilhamentoSessao,
  updateSessaoTags,
} from '@/api/sessoes'
import { absoluteUrl, getApiErrorMessage } from '@/api/client'
import type { SessaoGravacao } from '@/types/sessoes'
import { formatDateTime } from '@/utils/datetime'
import { formatOffsetMs, formatSessaoBytes, retentionDaysRemaining } from '@/utils/sessoes'

definePage({
  meta: {
    requiresAuth: true,
    requiresAdmin: true,
  },
})

const route = useRoute()
const router = useRouter()

const sessaoId = computed(() => {
  const { id } = route.params as { id?: string | string[] }
  return Array.isArray(id) ? (id[0] ?? '') : (id ?? '')
})

const isEmbed = computed(() => route.query.embed === '1')

const sessao = ref<SessaoGravacao | null>(null)
const events = ref<unknown[]>([])
const loadingMeta = ref(true)
const loadingEvents = ref(false)
const loadProgress = ref<number | null>(null)
const shareLoading = ref(false)
const detailsOpen = ref(false)

const replayerRef = ref<{ getCurrentTimeMs: () => number } | null>(null)
const tagDraft = ref('')
const tagsSaving = ref(false)
const noteText = ref('')
const noteOffsetMs = ref(0)
const noteSaving = ref(false)
const removingAnnotationId = ref<string | null>(null)

const usuarioLabel = computed(() => {
  if (!sessao.value) return '—'
  if (sessao.value.usuario_externo_nome) return sessao.value.usuario_externo_nome
  if (sessao.value.usuario_externo_id) return sessao.value.usuario_externo_id
  return '—'
})

const metadadosEntries = computed(() => {
  if (!sessao.value) return []
  return Object.entries(sessao.value.metadados ?? {})
})

const publicShareUrl = computed(() => {
  if (!sessao.value?.share_token) return ''
  return absoluteUrl(`/share/${sessao.value.share_token}`)
})

const popupUrl = computed(() => {
  if (isEmbed.value || !sessaoId.value) return ''
  return absoluteUrl(`/admin/sessoes/${sessaoId.value}?embed=1`)
})

const exportMeta = computed(() => {
  if (!sessao.value) return null
  return { ...sessao.value } as Record<string, unknown>
})

const retentionHint = computed(() => {
  const dias = sessao.value?.projeto?.retencao_dias
  if (dias == null || !sessao.value?.started_at) return null
  const left = retentionDaysRemaining(sessao.value.started_at, dias, dias)
  if (left === null) return null
  if (left <= 0) return 'Expirada'
  return `Expira em ${left} dia(s)`
})

const annotationsSorted = computed(() => {
  const list = [...(sessao.value?.annotations ?? [])]
  return list.sort((a, b) => a.offset_ms - b.offset_ms)
})

async function load(): Promise<void> {
  loadingMeta.value = true
  loadingEvents.value = false
  loadProgress.value = null
  events.value = []

  try {
    const meta = await getSessao(sessaoId.value)
    sessao.value = meta
    loadingMeta.value = false
    loadingEvents.value = true
    loadProgress.value = 0
    await loadSessaoEventsIncremental(meta.id, meta.chunks_count, {
      onChunk: (chunk) => {
        events.value.push(...chunk)
      },
      onProgress: (loaded, total) => {
        loadProgress.value = total > 0 ? (loaded / total) * 100 : 100
      },
    })
  } catch (error) {
    sessao.value = null
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao carregar sessão',
      text: getApiErrorMessage(error, 'Não foi possível abrir a sessão'),
    })
    if (!isEmbed.value) {
      void router.push('/admin/sessoes')
    }
  } finally {
    loadingMeta.value = false
    loadingEvents.value = false
    loadProgress.value = null
  }
}

async function toggleShare(enabled: boolean): Promise<void> {
  if (!sessao.value) return

  shareLoading.value = true
  try {
    if (enabled) {
      const result = await ativarCompartilhamentoSessao(sessao.value.id)
      sessao.value = {
        ...sessao.value,
        share_enabled: true,
        share_token: result.share_token,
      }
    } else {
      await revogarCompartilhamentoSessao(sessao.value.id)
      sessao.value = {
        ...sessao.value,
        share_enabled: false,
      }
    }
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao alterar compartilhamento',
      text: getApiErrorMessage(error, 'Não foi possível atualizar o link público'),
    })
  } finally {
    shareLoading.value = false
  }
}

async function copyPublicLink(): Promise<void> {
  if (!publicShareUrl.value) return

  try {
    await navigator.clipboard.writeText(publicShareUrl.value)
    await Swal.fire({ icon: 'success', title: 'Link público copiado', timer: 1200, showConfirmButton: false })
  } catch {
    await Swal.fire({ icon: 'info', title: 'Copie o link manualmente', text: publicShareUrl.value })
  }
}

function exportSessionJson(): void {
  if (!sessao.value || events.value.length === 0) {
    return
  }

  const payload = JSON.stringify({ sessao: sessao.value, events: events.value }, null, 2)
  const blob = new Blob([payload], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `sessao-${sessaoId.value}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}

async function onBookmarkRequest(offsetMs: number): Promise<void> {
  if (!sessao.value) return

  try {
    const result = await addSessaoAnnotation(sessao.value.id, {
      type: 'bookmark',
      offset_ms: offsetMs,
      text: '',
    })
    sessao.value = result.sessao
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao salvar bookmark',
      text: getApiErrorMessage(error, 'Não foi possível criar a anotação'),
    })
  }
}

function addTagFromDraft(): void {
  if (!sessao.value) return
  const tag = tagDraft.value.trim()
  if (!tag) return
  const tags = [...(sessao.value.tags ?? [])]
  if (!tags.includes(tag)) {
    tags.push(tag)
    sessao.value = { ...sessao.value, tags }
  }
  tagDraft.value = ''
}

function removeTag(tag: string): void {
  if (!sessao.value) return
  sessao.value = {
    ...sessao.value,
    tags: (sessao.value.tags ?? []).filter((t) => t !== tag),
  }
}

async function saveTags(): Promise<void> {
  if (!sessao.value) return
  tagsSaving.value = true
  try {
    sessao.value = await updateSessaoTags(sessao.value.id, sessao.value.tags ?? [])
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao salvar tags',
      text: getApiErrorMessage(error, 'Não foi possível atualizar as tags'),
    })
  } finally {
    tagsSaving.value = false
  }
}

function useCurrentOffset(): void {
  noteOffsetMs.value = replayerRef.value?.getCurrentTimeMs() ?? 0
}

async function addNote(): Promise<void> {
  if (!sessao.value) return
  const text = noteText.value.trim()
  if (!text) return

  noteSaving.value = true
  try {
    const result = await addSessaoAnnotation(sessao.value.id, {
      type: 'note',
      offset_ms: Math.max(0, Math.round(noteOffsetMs.value)),
      text,
    })
    sessao.value = result.sessao
    noteText.value = ''
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao salvar nota',
      text: getApiErrorMessage(error, 'Não foi possível criar a anotação'),
    })
  } finally {
    noteSaving.value = false
  }
}

async function removeAnnotation(annotationId: string): Promise<void> {
  if (!sessao.value) return
  removingAnnotationId.value = annotationId
  try {
    sessao.value = await removeSessaoAnnotation(sessao.value.id, annotationId)
  } catch (error) {
    await Swal.fire({
      icon: 'error',
      title: 'Erro ao remover anotação',
      text: getApiErrorMessage(error, 'Não foi possível remover'),
    })
  } finally {
    removingAnnotationId.value = null
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div v-if="isEmbed" class="min-vh-100 bg-light d-flex flex-column">
    <header class="border-bottom bg-white px-3 py-2">
      <AppLogo />
    </header>
    <main class="flex-grow-1 p-2 p-md-3">
      <div v-if="loadingMeta" class="text-muted">Carregando sessão…</div>
      <SessaoReplayer
        v-else-if="sessao"
        ref="replayerRef"
        :events="events"
        :loading="loadingEvents"
        :load-progress="loadProgress"
        :export-meta="exportMeta"
        :annotations="sessao.annotations ?? []"
        :show-timeline="false"
        compact
        @bookmark-request="onBookmarkRequest"
      />
    </main>
  </div>

  <AdminLayout v-else>
    <template #title>
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-2">
        <div>
          <RouterLink
            to="/admin/sessoes"
            class="small text-decoration-none"
          >
            <i class="bi bi-arrow-left me-1" aria-hidden="true" />
            Sessões
          </RouterLink>
          <h1 class="h5 mb-1 mt-1">Replay da sessão</h1>
          <p class="text-muted small mb-0">
            {{ usuarioLabel }}
            <span v-if="sessao">
              · {{ formatDateTime(sessao.started_at) ?? '—' }}
              <span v-if="sessao.ended_at">
                → {{ formatDateTime(sessao.ended_at) }}
              </span>
              · {{ sessao.status }} · {{ formatSessaoBytes(sessao.events_bytes) }}
            </span>
          </p>
          <p v-if="retentionHint" class="text-muted small mb-0 mt-1">
            <i class="bi bi-hourglass-split me-1" aria-hidden="true" />
            {{ retentionHint }}
          </p>
          <div v-if="sessao" class="d-flex flex-wrap gap-1 mt-2">
            <span class="badge text-bg-light border">{{ sessao.projeto?.nome ?? sessao.projeto_id }}</span>
            <span
              v-for="tag in sessao.tags ?? []"
              :key="tag"
              class="badge text-bg-primary"
            >
              {{ tag }}
            </span>
            <span
              v-for="[chave, valor] in metadadosEntries.slice(0, 4)"
              :key="chave"
              class="badge text-bg-light border"
            >
              {{ chave }}: {{ valor }}
            </span>
            <button
              type="button"
              class="btn btn-link btn-sm p-0 align-baseline"
              @click="detailsOpen = !detailsOpen"
            >
              {{ detailsOpen ? 'Ocultar detalhes' : 'Detalhes' }}
            </button>
          </div>
        </div>
        <div v-if="sessao" class="d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
          <div class="form-check form-switch mb-0">
            <input
              id="share_toggle"
              class="form-check-input"
              type="checkbox"
              role="switch"
              :checked="sessao.share_enabled"
              :disabled="shareLoading"
              @change="toggleShare(($event.target as HTMLInputElement).checked)"
            >
            <label class="form-check-label small" for="share_toggle">Link público</label>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="!sessao.share_enabled || !publicShareUrl"
            @click="copyPublicLink"
          >
            Copiar link
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="events.length === 0"
            @click="exportSessionJson"
          >
            Exportar JSON
          </button>
        </div>
      </div>
    </template>

    <div v-if="loadingMeta" class="text-muted">Carregando sessão…</div>

    <template v-else-if="sessao">
      <div
        v-if="detailsOpen"
        class="card shadow-sm border-0 mb-3"
      >
        <div class="card-body small">
          <div class="row g-2">
            <div class="col-md-4"><strong>Projeto:</strong> {{ sessao.projeto?.nome ?? sessao.projeto_id }}</div>
            <div class="col-md-4"><strong>Usuário:</strong> {{ usuarioLabel }}</div>
            <div v-if="sessao.usuario_externo_id" class="col-md-4">
              <strong>ID externo:</strong> {{ sessao.usuario_externo_id }}
            </div>
            <div class="col-12"><strong>User-Agent:</strong> {{ sessao.user_agent || '—' }}</div>
            <div class="col-12 text-truncate"><strong>URL:</strong> {{ sessao.url_inicial || '—' }}</div>
          </div>
          <div v-if="metadadosEntries.length > 0" class="mt-2">
            <strong>Metadados</strong>
            <ul class="list-unstyled mb-0 mt-1">
              <li v-for="[chave, valor] in metadadosEntries" :key="chave">
                <code>{{ chave }}</code>: {{ valor }}
              </li>
            </ul>
          </div>
          <div v-if="sessao.share_enabled && publicShareUrl" class="mt-2">
            <strong>Link público</strong>
            <div><code class="small">{{ publicShareUrl }}</code></div>
          </div>
        </div>
      </div>

      <SessaoReplayer
        ref="replayerRef"
        :events="events"
        :loading="loadingEvents"
        :load-progress="loadProgress"
        :export-meta="exportMeta"
        :popup-url="popupUrl"
        :annotations="sessao.annotations ?? []"
        @bookmark-request="onBookmarkRequest"
      />

      <div class="row g-3 mt-1">
        <div class="col-lg-5">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h2 class="h6 mb-3">Tags</h2>
              <div class="d-flex flex-wrap gap-1 mb-2">
                <span
                  v-for="tag in sessao.tags ?? []"
                  :key="tag"
                  class="badge text-bg-primary d-inline-flex align-items-center gap-1"
                >
                  {{ tag }}
                  <button
                    type="button"
                    class="btn-close btn-close-white"
                    style="font-size: 0.55rem"
                    :aria-label="`Remover tag ${tag}`"
                    @click="removeTag(tag)"
                  />
                </span>
                <span v-if="!(sessao.tags ?? []).length" class="text-muted small">Nenhuma tag</span>
              </div>
              <div class="input-group input-group-sm mb-2">
                <input
                  v-model="tagDraft"
                  type="text"
                  class="form-control"
                  placeholder="Nova tag"
                  @keydown.enter.prevent="addTagFromDraft"
                >
                <button type="button" class="btn btn-outline-secondary" @click="addTagFromDraft">
                  Adicionar
                </button>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="tagsSaving"
                @click="saveTags"
              >
                Salvar tags
              </button>
            </div>
          </div>
        </div>

        <div class="col-lg-7">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h2 class="h6 mb-3">Anotações</h2>
              <ul v-if="annotationsSorted.length > 0" class="list-group list-group-flush mb-3">
                <li
                  v-for="ann in annotationsSorted"
                  :key="ann.id"
                  class="list-group-item px-0 d-flex justify-content-between align-items-start gap-2"
                >
                  <div class="small">
                    <span
                      class="badge me-1"
                      :class="ann.type === 'bookmark' ? 'text-bg-warning' : 'text-bg-info'"
                    >
                      {{ ann.type === 'bookmark' ? 'Bookmark' : 'Nota' }}
                    </span>
                    <span class="text-muted font-monospace">{{ formatOffsetMs(ann.offset_ms) }}</span>
                    <div v-if="ann.text" class="mt-1">{{ ann.text }}</div>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="removingAnnotationId === ann.id"
                    @click="removeAnnotation(ann.id)"
                  >
                    Remover
                  </button>
                </li>
              </ul>
              <p v-else class="text-muted small">Nenhuma anotação.</p>

              <label class="form-label small" for="note_text">Nova nota</label>
              <textarea
                id="note_text"
                v-model="noteText"
                class="form-control form-control-sm mb-2"
                rows="2"
                placeholder="Texto da nota"
              />
              <div class="d-flex flex-wrap align-items-end gap-2">
                <div>
                  <label class="form-label small mb-1" for="note_offset">Offset (ms)</label>
                  <input
                    id="note_offset"
                    v-model.number="noteOffsetMs"
                    type="number"
                    min="0"
                    step="100"
                    class="form-control form-control-sm"
                    style="width: 8rem"
                  >
                </div>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="useCurrentOffset">
                  Usar tempo atual
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  :disabled="noteSaving || !noteText.trim()"
                  @click="addNote"
                >
                  Adicionar nota
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </AdminLayout>
</template>
